import { useContext } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FileUploadBody from "../../../../components/form/FileUploadBody";
import PageContentWrapper from "../../../../components/wrappers/PageContentWrapper";
import { spreadsheetTypes } from "../../../../enums/Consts";
import useConfirm from "../../../../hooks/useConfirm";
import { useRedirectIfNotAdminOrEmployee } from "../../../../hooks/useRedirectIfNotOwnClinic";
import logger from "../../../../utils/logger";
import useSubmit from "../../../master/species/hooks/useSubmit";
import UserImportTable, { parseName } from "../components/UserImportTable";
import { useSpreadsheetImport } from "../hooks/useSpreadsheetImport";
import { useImportConflicts } from "../hooks/useImportConflicts";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../../hooks/useSnackbar";
import { IMPORT_USERS } from "../../../../enums/ApiPaths";
import { parseImportedBirthDate } from "../utils/parseAmbulantniDnevnik";

export default function ImportUsersView() {
  useRedirectIfNotAdminOrEmployee();
  const methods = useForm();
  const { t } = useTranslation();
  const url = IMPORT_USERS;
  const submission = useSubmit(null, url, null);
  const { handleSnackbar } = useContext(SnackbarContext);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    null,
    t("Yes"),
    t("No"),
  );

  const {
    ConfirmationDialog: ImportUsersDialog,
    confirm: importUsersConfirm,
    getMessage: importUsersMsg,
  } = useConfirm(null, t("Yes"), t("Cancel"));

  // Resolves to the ids of the users that were actually created, so the table can drop them
  async function handleSubmit(data) {
    if (!data) return [];
    const payload = formatPayload(data);
    try {
      importUsersMsg(t("ImportUsersConfirm", { total: payload.length }));
      const confirmAns = await importUsersConfirm();

      if (confirmAns) {
        getMessage(t("SendEmailToImportedUsersConf"));
        const ans = await confirm();
        submission.setUpdatedUrl(url + "/" + ans);
        const imported = await submission.submitCallback(payload);
        if (!imported) return [];

        // Rows the backend skipped (a user with the same name and OIB already exists) stay
        const skippedRows = new Set(
          submission.getLastResponse()?.data?.skippedRows ?? [],
        );
        if (skippedRows.size > 0) {
          handleSnackbar(
            t("ImportSkippedExisting", { count: skippedRows.size }),
            snackbarSeverity.WARNING,
            "filled",
            true,
            false,
          );
        }
        return data
          .filter((_, index) => !skippedRows.has(index))
          .map((user) => user.id);
      }
    } catch (e) {
      logger.error(e);
    }
    return [];
  }

  return (
    <PageContentWrapper>
      <FormProvider {...methods}>
        <ImportUsersContent handleSubmit={(data) => handleSubmit(data)} />
      </FormProvider>
      <ConfirmationDialog />
      <ImportUsersDialog />
    </PageContentWrapper>
  );
}

function ImportUsersContent({ handleSubmit }) {
  const { t } = useTranslation();
  const {
    status,
    error,
    users,
    includedUsers,
    excludedIds,
    toggleUser,
    updateUser,
    removeAllUsers,
    addAllUsers,
    parseFile,
    removeUsers,
  } = useSpreadsheetImport();
  // A taken email/phone number makes the backend reject the whole import, so it is
  // flagged here and blocks the button until fixed or the user is excluded
  const {
    conflicts,
    conflictCount,
    isChecking,
    isError: conflictCheckFailed,
  } = useImportConflicts(users, includedUsers);

  // status: "idle" | "ready" | "loading" | "done"
  const showDropzone =
    status === "idle" || status === "ready" || status === "loading";
  const showTable = status === "done";

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {showDropzone && (
        <Stack spacing={2}>
          <AddSpreadSheet />

          {status === "loading" && (
            <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
              <CircularProgress size={28} />
              <Typography color="text.secondary">{t("Loading")}</Typography>
            </Stack>
          )}

          {status === "ready" && (
            <Box>
              <Button variant="contained" onClick={parseFile}>
                {t("Confirm")}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {showTable && (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t("ImportSummary", {
              total: users.length,
              included: includedUsers.length,
            })}
          </Typography>

          {conflictCount > 0 && (
            <Alert severity="error" data-testid="import.conflicts-alert">
              {t("ImportConflictsBlock", { count: conflictCount })}
            </Alert>
          )}
          {conflictCheckFailed && (
            <Alert severity="warning">{t("ImportConflictsCheckFailed")}</Alert>
          )}

          <UserImportTable
            users={users}
            excludedIds={excludedIds}
            onToggleUser={toggleUser}
            onUpdateUser={updateUser}
            removeAll={removeAllUsers}
            addAllUsers={addAllUsers}
            conflicts={conflicts}
          />

          <Box>
            <Button
              variant="contained"
              disabled={
                includedUsers.length === 0 || conflictCount > 0 || isChecking
              }
              onClick={async () =>
                removeUsers(await handleSubmit(includedUsers))
              }
            >
              {t("ImportUsersButton", {
                total: includedUsers.length,
              })}
            </Button>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

function AddSpreadSheet() {
  return (
    <FileUploadBody
      message="ImportUsers"
      allowedFileTypes={spreadsheetTypes}
      numberOfFileLimit={1}
      key={"importing-users-file-upload"}
    />
  );
}

function formatPayload(data) {
  return data?.map((user) => {
    const [first, ...rest] = user?.name?.split(" ") ?? [];
    const firstName = first ?? "";
    const lastName = rest.join(" ");
    return {
      pets: formatPetData(user.pets),
      oib: user.oib,
      firstName: parseName(firstName),
      lastName: parseName(lastName),
      address: user.address,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    };
  });
}

function formatPetData(usersPets) {
  return usersPets?.map((pet) => {
    const birthDate = parseImportedBirthDate(pet?.birthDate);
    return {
      birtDate: birthDate ? birthDate.format("DD/MM/YYYY") : null,
      breed: pet?.breed,
      microchip: pet?.microchip,
      name: pet?.name,
      passport: pet?.passport,
      species: pet?.species,
      aggression: pet?.aggression,
      bloodType: pet?.bloodType,
      description: pet?.description,
      gender: pet?.gender,
    };
  });
}
